/**
 * @file FX-bus для онлайн-белота.
 *
 * Превращает поток снапшотов `belotArmenian` (Colyseus `BelotRoom`) в
 * локальные FX-вызовы (звуки + reveal overlay), не требуя серверных
 * изменений. Опирается одновременно на:
 *
 *  - **Снапшот-дифы** между предыдущим и текущим `belotArmenian`
 *  - **`lastEvents`** — payload для reveal-оверлея
 *
 * Защита от залпа звуков при «догоняющих» патчах: троттлинг по категориям.
 * Таймеры снимаются при размонтировании и при новом landing-таймере карты.
 *
 * @module components/online/hooks/useBelotOnlineFxBus
 */

import { useEffect, useRef } from 'react';
import { belotCardIsTrump, belotContractCpLabelFromEngine } from '../../../game/belotArmenianCards';

/** Длительность reveal-оверлея в мс — синхронизировано с `AUCTION_REVEAL_MS` локального лобби. */
export const ONLINE_BELOT_AUCTION_REVEAL_MS = 2350;

const isFn = (v) => typeof v === 'function';

function teamOfSeat(seat) {
  return seat === 0 || seat === 2 ? 0 : 1;
}

/** Минимальный интервал между звуками одной категории (лаг / несколько commit подряд). */
const THROTTLE_MS = {
  table: 52,
  flip: 86,
  trick: 108,
  shuffle: 240,
  contra: 72,
  notify: 380,
  end: 280,
};

/**
 * @typedef {{
 *   token: string,
 *   points: number,
 *   trump: unknown,
 *   multiplier: number,
 *   bidAuthorSeat: number|null,
 *   fromX: number,
 *   fromY: number,
 * }} BelotOnlineRevealFx
 */

/**
 * @param {object} args
 * @param {object|null} args.belotArmenian — публичный снапшот `belotArmenian` из `GAME_STATE`.
 * @param {(soundId: string, volume?: number) => void} args.play
 * @param {(reveal: BelotOnlineRevealFx) => void} [args.onAuctionEnd] — триггер reveal-оверлея.
 * @param {React.MutableRefObject<HTMLElement|null>} [args.contractBadgeAnchorRef]
 * @param {number|null} [args.mySeat] — место героя для звука победы на `match_end`.
 * @param {boolean} [args.suppressed] — legacy: полный sync-only (как syncOnlyMode).
 * @param {boolean} [args.syncOnlyMode] — только синхронизация prev, без звуков и без onAuctionEnd (реконнект).
 * @param {boolean} [args.audioSuppressed] — не вызывать `play`; дифы и onAuctionEnd обрабатываются.
 */
export function useBelotOnlineFxBus({
  belotArmenian,
  play,
  onAuctionEnd,
  contractBadgeAnchorRef,
  mySeat = null,
  suppressed = false,
  syncOnlyMode: syncOnlyModeProp,
  audioSuppressed: audioSuppressedProp,
}) {
  const syncOnlyMode = syncOnlyModeProp !== undefined ? syncOnlyModeProp : suppressed;
  const audioSuppressed = audioSuppressedProp !== undefined ? audioSuppressedProp : suppressed;
  const prevRef = useRef({
    initialized: false,
    phase: null,
    roundIndex: null,
    contractKey: null,
    currentTrickLen: 0,
    completedTrickIdx: -1,
    contraMultiplier: 1,
    teamScores: [0, 0],
    matchEnded: false,
  });
  const lastRevealKeyRef = useRef('');
  const throttleAtRef = useRef(/** @type {Record<string, number>} */ ({}));
  /** Только unmount: при смене `belotArmenian` не отменяем отложенные FX (иначе пропадают flips). */
  const pendingTimeoutsRef = useRef(/** @type {Set<ReturnType<typeof setTimeout>>} */ (new Set()));
  const landingTimeoutRef = useRef(/** @type {ReturnType<typeof setTimeout>|null} */ (null));

  useEffect(
    () => () => {
      pendingTimeoutsRef.current.forEach((id) => clearTimeout(id));
      pendingTimeoutsRef.current.clear();
      if (landingTimeoutRef.current != null) {
        clearTimeout(landingTimeoutRef.current);
        landingTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    const schedule = (fn, ms) => {
      const id = window.setTimeout(() => {
        pendingTimeoutsRef.current.delete(id);
        fn();
      }, ms);
      pendingTimeoutsRef.current.add(id);
      return id;
    };

    const throttledPlay = (soundId, vol, category) => {
      if (audioSuppressed) return;
      const gap = THROTTLE_MS[category] ?? 60;
      const now = Date.now();
      const last = throttleAtRef.current[category] ?? 0;
      if (now - last < gap) return;
      throttleAtRef.current[category] = now;
      play(soundId, vol);
    };

    /**
     * @param {unknown} trumpSuit
     * @param {unknown} card
     * @param {{ landing?: boolean }} [opts]
     */
    const playCardPlayedToTrickSounds = (trumpSuit, card, opts = {}) => {
      const landing = opts.landing !== false;
      if (card == null) return;
      if (landingTimeoutRef.current != null) {
        const lid = landingTimeoutRef.current;
        clearTimeout(lid);
        pendingTimeoutsRef.current.delete(lid);
        landingTimeoutRef.current = null;
      }
      const isTr = trumpSuit != null && belotCardIsTrump(trumpSuit, card);
      throttledPlay(isTr ? 'belot-card-table-trump' : 'belot-card-table', 0.78, 'table');
      if (!landing) return;
      landingTimeoutRef.current = schedule(() => {
        landingTimeoutRef.current = null;
        throttledPlay('card-flip', 0.52, 'flip');
        throttledPlay(isTr ? 'belot-card-table-trump' : 'belot-card-table', 0.58, 'table');
      }, 190);
    };

    if (!belotArmenian || typeof belotArmenian !== 'object') {
      return undefined;
    }

    const prev = prevRef.current;
    const phase = String(belotArmenian.phase || '');
    const roundIndex = Number(belotArmenian.roundIndex ?? 0);
    const contract = belotArmenian.contract || null;
    const contractKey = contract
      ? `${contract.bidAuthorSeat ?? 'x'}:${Number(contract.tens || 0)}:${String(contract.trump)}:${Number(contract.contraMultiplier || 1)}:${roundIndex}`
      : null;
    const currentTrick = Array.isArray(belotArmenian.currentTrick)
      ? belotArmenian.currentTrick
      : [];
    const completedIdx = Number(belotArmenian.lastCompletedTrick?.trickIndex ?? -1);
    const auctionMult = Number(belotArmenian.auction?.contraMultiplier || 1);
    const contractMult = Number(contract?.contraMultiplier || 1);
    const contraMultiplier = Math.max(auctionMult, contractMult);
    const teamScores = Array.isArray(belotArmenian.teamScores)
      ? [Number(belotArmenian.teamScores[0]) || 0, Number(belotArmenian.teamScores[1]) || 0]
      : [0, 0];
    const events = Array.isArray(belotArmenian.lastEvents) ? belotArmenian.lastEvents : [];

    if (!prev.initialized) {
      prevRef.current = {
        initialized: true,
        phase,
        roundIndex,
        contractKey,
        currentTrickLen: currentTrick.length,
        completedTrickIdx: completedIdx,
        contraMultiplier,
        teamScores,
        matchEnded: phase === 'match_end',
      };
      return undefined;
    }

    if (syncOnlyMode) {
      prevRef.current = {
        initialized: true,
        phase,
        roundIndex,
        contractKey,
        currentTrickLen: currentTrick.length,
        completedTrickIdx: completedIdx,
        contraMultiplier,
        teamScores,
        matchEnded: phase === 'match_end',
      };
      return undefined;
    }

    /** @type {(type: string) => Array<Record<string, unknown>>} */
    const eventsOf = (type) => events.filter((ev) => ev && ev.type === type).map((ev) => ev.payload || {});

    if (roundIndex !== prev.roundIndex) {
      throttledPlay('belot-shuffle-deal', 0.42, 'shuffle');
    }

    const trumpSuit = contract?.trumpSuit ?? null;

    if (currentTrick.length > prev.currentTrickLen && phase === 'play') {
      const last = currentTrick[currentTrick.length - 1];
      playCardPlayedToTrickSounds(trumpSuit, last?.card);
    } else if (
      phase === 'play' &&
      prev.currentTrickLen >= 3 &&
      currentTrick.length === 0 &&
      completedIdx > prev.completedTrickIdx
    ) {
      const plays = belotArmenian.lastCompletedTrick?.plays;
      const lastPlay = Array.isArray(plays) && plays.length ? plays[plays.length - 1] : null;
      playCardPlayedToTrickSounds(trumpSuit, lastPlay?.card, { landing: false });
    }

    if (completedIdx > prev.completedTrickIdx) {
      throttledPlay('belot-trick-collect', 0.48, 'trick');
      schedule(() => throttledPlay('card-flip', 0.48, 'flip'), 280);
    }

    const auctionEndedEvents = eventsOf('AUCTION_ENDED');
    const contractAppearedFromDiff =
      contractKey != null && contractKey !== prev.contractKey;
    if (
      isFn(onAuctionEnd) &&
      contractAppearedFromDiff &&
      lastRevealKeyRef.current !== contractKey
    ) {
      lastRevealKeyRef.current = contractKey;
      const auctionPayload = auctionEndedEvents[0] || {};
      const bid = auctionPayload.bid && typeof auctionPayload.bid === 'object'
        ? auctionPayload.bid
        : (contract ? { tens: contract.tens, trump: contract.trump, kapot: contract.kapot } : null);
      const points = belotContractCpLabelFromEngine({
        contractTargetPoints: contract?.contractTargetPoints,
        kapot: bid?.kapot ?? contract?.kapot,
        tens: bid?.tens ?? contract?.tens,
      });
      const trump = bid?.trump ?? contract?.trump ?? null;
      const multiplier = Math.max(1, contraMultiplier);
      const bidAuthorSeat =
        typeof auctionPayload.bidAuthorSeat === 'number'
          ? auctionPayload.bidAuthorSeat
          : (typeof contract?.bidAuthorSeat === 'number' ? contract.bidAuthorSeat : null);
      const anchorRect = contractBadgeAnchorRef?.current?.getBoundingClientRect?.();
      const viewportCx = typeof window !== 'undefined' ? window.innerWidth / 2 : 0;
      const viewportCy = typeof window !== 'undefined' ? window.innerHeight / 2 : 0;
      const anchorCx = anchorRect ? anchorRect.left + anchorRect.width / 2 : viewportCx;
      const anchorCy = anchorRect ? anchorRect.top + anchorRect.height / 2 : viewportCy;
      const fromX = anchorCx - viewportCx;
      const fromY = anchorCy - viewportCy;
      onAuctionEnd({
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        points: points === '—' ? '—' : points,
        trump,
        multiplier,
        bidAuthorSeat,
        fromX,
        fromY,
      });
      throttledPlay('belot-contra-hit', 0.68, 'contra');
    }

    if (prev.contraMultiplier === 1 && contraMultiplier === 2) {
      throttledPlay('belot-contra-hit', 0.62, 'contra');
    } else if (prev.contraMultiplier === 2 && contraMultiplier >= 4) {
      throttledPlay('belot-contra-hit', 0.78, 'contra');
    }

    if (
      (teamScores[0] !== prev.teamScores[0] || teamScores[1] !== prev.teamScores[1]) &&
      prev.phase !== phase &&
      (phase === 'score' || phase === 'init' || phase === 'match_end')
    ) {
      throttledPlay('notification', 0.42, 'notify');
    }

    if (phase === 'match_end' && !prev.matchEnded) {
      const s0 = teamScores[0];
      const s1 = teamScores[1];
      let winTeam = null;
      if (s0 > s1) winTeam = 0;
      else if (s1 > s0) winTeam = 1;
      const meWins =
        mySeat != null && winTeam != null && teamOfSeat(mySeat) === winTeam;
      throttledPlay(meWins ? 'winner-game' : 'overlay-money-counting', 0.6, 'end');
    }

    prevRef.current = {
      initialized: true,
      phase,
      roundIndex,
      contractKey,
      currentTrickLen: currentTrick.length,
      completedTrickIdx: completedIdx,
      contraMultiplier,
      teamScores,
      matchEnded: phase === 'match_end',
    };

    return undefined;
  }, [belotArmenian, play, onAuctionEnd, contractBadgeAnchorRef, mySeat, syncOnlyMode, audioSuppressed]);
}

